"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireWildcard(require("react"));
var _reactDom = _interopRequireDefault(require("react-dom"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _reactFocusLock = _interopRequireDefault(require("react-focus-lock"));
var _classnames = _interopRequireDefault(require("classnames"));
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && Object.prototype.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
/* eslint-disable max-statements */
/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
// The keyboard listener is added to the document and while this is presentational, the contents are not.

const UX_DISRUPT_CLASSNAME = 'ux-disrupt';
const UX_DISRUPT_FOCUSLOCK_DEFAULTS = {
  autoFocus: true,
  disabled: false,
  returnFocus: true
};
const UX_DISRUPT_SHADOWROOT_MSG = 'Warning: <Disrupt portalElem={ ShadowRoot }/> is not supported; use a DOM Element instead.';

/**
 * Toggles the data-inert attribute, for managing multiple possible disruptions
 *
 * @param {Element} parent - The parent of the component
 * @param {Boolean} inert - Determines if the inert attribute should be added or removed
 */
function manageInert(parent, inert) {
  const amount = Number(inert) * 2 - 1;
  const value = Number(parent.getAttribute('data-inert')) + amount;
  if (value) {
    parent.setAttribute('data-inert', value);
  } else {
    parent.removeAttribute('data-inert');
  }
}

/**
 * Create a <Disrupt/> component
 *
 * @param {Object} props - Properties to inform the component
 * @param {String} [props.className] - Class name for positioning
 * @param {Elements} [props.children] - Given children for the content container
 * @param {Boolean|Object} [props.focusLock] - Can either toggle focus lock or configure it
 * @param {Function} [props.onClose] - Callback which fires when the user attempts to leave
 * @param {Function} [prop.onEventBehind] - Callback which fires when an event occurs behind the component
 * @param {Element} [prop.portalElem] - The element to attach the component
 * @param {String} [prop.strategy] - The method that the backdrop is attached
 * @param {Boolean} [props.stretch] - Occupy the full width of the container
 * @param {Boolean} [props.fullScreenChildren] - Disables default styles to allow children to fill the screen
 * @returns {Component} - The Disrupt component
 */
function Disrupt(_ref) {
  let {
    children,
    className,
    focusLock = false,
    onClose,
    onEventBehind,
    portalElem,
    strategy,
    stretch,
    fullScreenChildren = false
  } = _ref;
  const style = {
    position: strategy || 'absolute'
  };
  const componentRef = (0, _react.useRef)(null);
  const pointerRef = (0, _react.useRef)({
    isPointerDown: false
  });
  function isValidEvent(ev) {
    return ev.target === ev.currentTarget && ev.target === componentRef.current;
  }
  const handlePointerDown = (0, _react.useCallback)(function _handlePointerDown(ev) {
    if (isValidEvent(ev)) {
      pointerRef.current.isPointerDown = true;
    }
  }, [pointerRef]);
  const handlePointerUp = (0, _react.useCallback)(function _handlePointerUp(ev) {
    if (pointerRef.current.isPointerDown && isValidEvent(ev)) {
      typeof onClose === 'function' && onClose(ev);
    }
  }, [pointerRef, onClose]);

  /**
   * handleKeyDown - only fires the onClose event when escape key is pressed on backdrop
   * @param {React.SyntheticEvent} ev - onKeyDown event
   */
  const handleKeyDown = (0, _react.useCallback)(function onKeyDown(ev) {
    if (ev.key !== 'Escape') return;
    ev.stopPropagation();
    typeof onClose === 'function' && onClose(ev);
  }, [onClose]);

  /**
   * handleOnScrollCapture - resets the pointerRef's isPointerDown property to allow users to scroll with the pointer
   * outside of the content without triggering onClose
   */
  const handleOnScrollCapture = (0, _react.useCallback)(function onScrollCapture() {
    pointerRef.current.isPointerDown = false;
  }, [pointerRef]);
  let focusLockProps = {
    disabled: true
  };
  if (focusLock) {
    focusLockProps = typeof focusLock === 'boolean' ? UX_DISRUPT_FOCUSLOCK_DEFAULTS : focusLock;
  }
  (0, _react.useLayoutEffect)(function effect() {
    var _componentRef$current, _window;
    // Determine the element where the component is attached
    const parent = portalElem || ((_componentRef$current = componentRef.current) === null || _componentRef$current === void 0 ? void 0 : _componentRef$current.parentElement);

    // Cannot add functionality when mounted to shadow root
    if ((_window = window) !== null && _window !== void 0 && _window.ShadowRoot && parent instanceof window.ShadowRoot) {
      process.env.NODE_ENV !== 'production' && console.warn(UX_DISRUPT_SHADOWROOT_MSG);
      return;
    }

    // Function for catching events that occur behind the disruption
    function eventBehind(ev) {
      const insideParent = parent.contains(ev.target);
      const insideComponent = componentRef.current.contains(ev.target);
      if (insideParent && !insideComponent) onEventBehind(ev);
    }

    // Set a data-attribute on the parent that _shouldn't_ be clobbered by re-renders
    manageInert(parent, true);

    // Listen to all the things
    typeof onEventBehind === 'function' && parent.addEventListener('click', eventBehind);
    typeof onEventBehind === 'function' && parent.addEventListener('focusin', eventBehind);
    return function teardown() {
      var _window2;
      if ((_window2 = window) !== null && _window2 !== void 0 && _window2.ShadowRoot && parent instanceof window.ShadowRoot) return;

      // Teardown
      manageInert(parent, false);
      parent.removeEventListener('click', eventBehind);
      parent.removeEventListener('focusin', eventBehind);
    };
  }, [portalElem, onEventBehind, onClose]);
  const classNames = [`${UX_DISRUPT_CLASSNAME}-content`];
  if (stretch) {
    classNames.push(`${UX_DISRUPT_CLASSNAME}--stretch`);
  }
  const component = /*#__PURE__*/_react.default.createElement("div", {
    "data-version": "2400",
    className: (0, _classnames.default)(`${UX_DISRUPT_CLASSNAME}-backdrop`, className, {
      [`${UX_DISRUPT_CLASSNAME}--fullscreen-children`]: fullScreenChildren
    }),
    ref: componentRef,
    onPointerDown: handlePointerDown,
    onPointerUp: handlePointerUp,
    onKeyDown: handleKeyDown,
    onScrollCapture: handleOnScrollCapture,
    style: style
  }, /*#__PURE__*/_react.default.createElement("div", {
    "data-version": "2400",
    className: classNames.join(' ')
  }, /*#__PURE__*/_react.default.createElement(_reactFocusLock.default, focusLockProps, /*#__PURE__*/_react.default.createElement("div", {
    tabIndex: -1
  }, children))));
  return portalElem ? /*#__PURE__*/_reactDom.default.createPortal(component, portalElem) : component;
}
Disrupt.propTypes = {
  /** Children to render within */
  children: _propTypes.default.node,
  /** Class name for positioning */
  className: _propTypes.default.string,
  /** Defaults to false. If `true`, enables focus lock with default options.
   * If object, passes props to react-focus-lock */
  focusLock: _propTypes.default.oneOfType([_propTypes.default.bool, _propTypes.default.object]),
  /** Callback that fires when the user attempts to leave the component */
  onClose: _propTypes.default.func,
  /** Callback that fires when an event occurs behind the component */
  onEventBehind: _propTypes.default.func,
  /** Element to attach the component to, use `document.body` for "modal" behavior */
  portalElem: _propTypes.default.object,
  /** Method to attach the backdrop */
  strategy: _propTypes.default.oneOf(['absolute', 'fixed']),
  /** Stretches the width of the container */
  stretch: _propTypes.default.bool,
  /** Determines whether to remove styles and allow fullscreen children to position themselves */
  fullScreenChildren: _propTypes.default.bool
};
var _default = exports.default = Disrupt;